// Validation utilities

export const validators = {
  email: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  otpCode: (code: string): boolean => {
    return /^\d{6}$/.test(code);
  },

  required: (value: any): boolean => {
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return value != null && value !== undefined;
  },

  minLength: (value: string, min: number): boolean => {
    return value.length >= min;
  },

  maxLength: (value: string, max: number): boolean => {
    return value.length <= max;
  },

  price: (value: number): boolean => {
    return value >= 0 && Number.isFinite(value);
  },

  url: (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  },
};

export const validateForm = <T extends Record<string, any>>(
  data: T,
  rules: Record<keyof T, Array<(value: any) => string | null>>
): Record<keyof T, string> | null => {
  const errors: Partial<Record<keyof T, string>> = {};

  for (const [field, fieldRules] of Object.entries(rules)) {
    const value = data[field as keyof T];
    
    for (const rule of fieldRules) {
      const error = rule(value);
      if (error) {
        errors[field as keyof T] = error;
        break; // Stop at first error for this field
      }
    }
  }

  return Object.keys(errors).length > 0 ? errors as Record<keyof T, string> : null;
};

// Common validation rules
export const rules = {
  required: (fieldName: string) => (value: any) =>
    validators.required(value) ? null : `${fieldName} is required`,

  email: (value: string) =>
    validators.email(value) ? null : 'Please enter a valid email address',

  otpCode: (value: string) =>
    validators.otpCode(value) ? null : 'OTP code must be 6 digits',

  minLength: (min: number, fieldName: string) => (value: string) =>
    validators.minLength(value, min) ? null : `${fieldName} must be at least ${min} characters`,

  maxLength: (max: number, fieldName: string) => (value: string) =>
    validators.maxLength(value, max) ? null : `${fieldName} must be less than ${max} characters`,

  price: (value: number) =>
    validators.price(value) ? null : 'Price must be a valid positive number',
};
